import { atomWithStorage } from 'jotai/utils';
import { focusAtom } from 'jotai-optics';
import { type Paths } from 'type-fest';

import type { AdvancedFiltersState } from '../../features';
import { type AdvancedFilterOperators } from '../../types';

export type FiltersStorage = Record<string, Array<string>>;

export type AdvancedFiltersStorage = {
  conditions: Array<{
    field: string;
    operator: AdvancedFilterOperators;
    value: string;
  }>;
  mode: 'and' | 'or' | null;
};
export type ProductDimensionId =
  | 'department'
  | 'sub_department'
  | 'brand'
  | 'class'
  | 'sub_class'
  | 'budget_level';
export type GeographicDimensionId =
  | 'budget_level'
  | 'region'
  | 'country'
  | 'location';

interface SessionStorage {
  replenishment: {
    productsTable: {
      advancedFilters: AdvancedFiltersState | null;
    };
    productLocationsTable: {
      advancedFilters: AdvancedFiltersState | null;
    };
    productSkusTable: {
      advancedFilters: AdvancedFiltersState | null;
    };
    locationsTable: {
      advancedFilters: AdvancedFiltersState | null;
    };
    locationSkusTable: {
      advancedFilters: AdvancedFiltersState | null;
    };
    locationProductsTable: {
      advancedFilters: AdvancedFiltersState | null;
    };
    locationDepartmentsTable: {
      advancedFilters: AdvancedFiltersState | null;
    };
    pivotTable: {
      advancedFilters: AdvancedFiltersState | null;
    };
  };
  reorder: {
    productsTable: {
      advancedFilters: AdvancedFiltersState | null;
    };
  };
  rebalancing: {
    pivotTable: {
      advancedFilters: AdvancedFiltersState | null;
    };
    locationTable: {
      advancedFilters: AdvancedFiltersState | null;
    };
    productTable: {
      advancedFilters: AdvancedFiltersState | null;
    };
    tripsTable: {
      advancedFilters: AdvancedFiltersState | null;
    };
  };
  insights: {
    selectedTableTab: string | null;
  };
}

export type SessionStorageKeys = Paths<SessionStorage>;

export const initialSessionStorageData: SessionStorage = {
  replenishment: {
    productsTable: {
      advancedFilters: null,
    },
    productLocationsTable: {
      advancedFilters: null,
    },
    productSkusTable: {
      advancedFilters: null,
    },
    locationsTable: {
      advancedFilters: null,
    },
    locationSkusTable: {
      advancedFilters: null,
    },
    locationProductsTable: {
      advancedFilters: null,
    },
    locationDepartmentsTable: {
      advancedFilters: null,
    },
    pivotTable: {
      advancedFilters: null,
    },
  },
  reorder: {
    productsTable: {
      advancedFilters: null,
    },
  },
  rebalancing: {
    pivotTable: {
      advancedFilters: null,
    },
    locationTable: {
      advancedFilters: null,
    },
    productTable: {
      advancedFilters: null,
    },
    tripsTable: {
      advancedFilters: null,
    },
  },
  insights: {
    selectedTableTab: null,
  },
};

const triggerEvent = (
  key: string,
  oldValue: string | null,
  newValue: string | null,
) => {
  const event = new StorageEvent('storage', {
    key,
    oldValue,
    newValue,
    storageArea: sessionStorage,
    url: window.location.href,
  });
  window.dispatchEvent(event);
};

const storage = {
  subscribe: (key: string, callback: (value: SessionStorage) => void) => {
    const storageEventCallback = (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        callback(JSON.parse(e.newValue));
      }
    };

    window.addEventListener('storage', storageEventCallback);
    return () => {
      window.removeEventListener('storage', storageEventCallback);
    };
  },
  getItem: function (key: string): SessionStorage {
    const value = sessionStorage.getItem(key);

    if (!value) {
      return initialSessionStorageData;
    }

    const parsedValue = JSON.parse(value);

    for (const [defaultKey, defaultValue] of Object.entries(
      initialSessionStorageData,
    )) {
      if (!(defaultKey in parsedValue)) {
        parsedValue[defaultKey] = defaultValue;
      }
    }

    return parsedValue;
  },
  setItem: function (key: string, newValue: SessionStorage): void {
    const oldValue = sessionStorage.getItem(key);
    const value = JSON.stringify(newValue);
    sessionStorage.setItem(key, value);

    triggerEvent(key, oldValue, value);
  },
  removeItem: function (key: string): void {
    const oldValue = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);

    triggerEvent(key, oldValue, null);
  },
};

export const sessionStorageAtom = atomWithStorage(
  'autone-session-storage-v4',
  initialSessionStorageData,
  storage,
  {
    getOnInit: true,
  },
);

export const getSessionStorageAtom = <K extends SessionStorageKeys>(key: K) => {
  return focusAtom(sessionStorageAtom, (optic) => optic.path(key));
};
